import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Paper, CircularProgress, Fade } from "@mui/material";
import { AccessTime } from "@mui/icons-material";
import PendingIcon from "@mui/icons-material/Pending";
import FactoryIcon from '@mui/icons-material/Factory';

const ICON_BG = {
  total: "linear-gradient(135deg, #4F8CFF 0%, #2355D8 100%)",
  active: "linear-gradient(135deg, #34D399 0%, #059669 100%)",
  pending: "linear-gradient(135deg, #F87171 0%, #B91C1C 100%)",
};

const SuperAdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    activeUsers: 0,
    pendingUsers: 0
  });

  useEffect(() => {
    setLoading(true);
    setError(null);

    const timer = setTimeout(() => {
      setDashboardData({
        totalUsers: 150,
        activeUsers: 120,
        pendingUsers: 30
      });
      setLoading(false);
    }, 1200);

    return () => clearTimeout(timer);
  }, []);

  const orderStatus = [
    {
      label: "Total Users",
      value: dashboardData.totalUsers,
      icon: <PendingIcon sx={{ fontSize: 38, color: "#fff" }} />,
      iconBg: ICON_BG.total,
      bgColor: "rgba(219, 225, 240, 0.7)",
      textColor: "#2355D8"
    },
    {
      label: "Active Users",
      value: dashboardData.activeUsers,
      icon: <AccessTime sx={{ fontSize: 38, color: "#fff" }} />,
      iconBg: ICON_BG.active,
      bgColor: "rgba(220, 255, 220, 0.7)",
      textColor: "#059669"
    },
    {
      label: "Pending Users",
      value: dashboardData.pendingUsers,
      icon: <FactoryIcon sx={{ fontSize: 38, color: "#fff" }} />,
      iconBg: ICON_BG.pending,
      bgColor: "rgba(255, 220, 220, 0.7)",
      textColor: "#B91C1C"
    }
  ];

  return (
    <Box
      sx={{
        ml: { xs: 2, sm: 4, md: 18, lg: 25 },
        mr: { xs: 2, sm: 4, md: 8, lg: 12 },
        mt: { xs: 8, sm: 10, md: 12, lg: 15 },
        mb: { xs: 4, sm: 6, md: 8 },
        width: 'auto',
        minHeight: '100vh',
        boxSizing: 'border-box',
      }}
    >
      <Fade in timeout={800}>
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              fontFamily: "Poppins, sans-serif",
              letterSpacing: 1,
              color: "#2563EB",
              mb: 1,
              ml: { xs: 2, sm: 4, md: 15, lg: 8 },
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            Welcome, Super Admin!
          </Typography>
        </Box>
      </Fade>

      <Typography
        variant="h5"
        fontWeight="bold"
        color="text.primary"
        gutterBottom
        sx={{
          fontSize: { xs: "1.5rem", sm: "1.5rem", md: "1.8rem" },
          textAlign: { xs: "center", sm: "left" },
          mb: 4,
          ml: { xs: 2, sm: 4, md: 15, lg: 8 },
        }}
      >
        Users Overview
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, justifyContent: { xs: "center", sm: "flex-start" } }}>
          <CircularProgress color="primary" size={32} />
          <Typography ml={{ xs: 2, sm: 4, md: 15, lg: 8 }}>Loading dashboard data...</Typography>
        </Box>
      ) : error ? (
        <Typography color="error">Error loading dashboard data: {error?.message}</Typography>
      ) : (
        <Grid container spacing={3} justifyContent="center">
          {orderStatus.map((status, index) => (
            <Grid item key={status.id} xs={12} sm={6} md={4}>
              <Fade in timeout={600 + index * 200}>
                <Paper
                  elevation={6}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    p: { xs: 2.5, sm: 3.5 },
                    borderRadius: 3,
                    bgcolor: status.bgColor,
                    boxShadow: "0 8px 24px 0 rgba(57,106,255,0.08)",
                    flexDirection: { xs: "column", sm: "row" },
                    textAlign: { xs: "center", sm: "left" },
                    height: 'auto',
                    minHeight: { sm: '150px' },
                    transition: "transform 0.2s, box-shadow 0.2s",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-6px) scale(1.03)",
                      boxShadow: "0 16px 32px 0 rgba(57,106,255,0.13)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      background: status.iconBg,
                      boxShadow: "0 4px 16px 0 rgba(57,106,255,0.10)",
                      mb: { xs: 1, sm: 0 },
                    }}
                  >
                    {status.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: status.textColor,
                        fontWeight: 600,
                        fontFamily: "Poppins, sans-serif",
                        mb: 0.5,
                        letterSpacing: 0.5,
                        fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                      }}
                    >
                      {status.label}
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight={800}
                      sx={{
                        color: "#222",
                        fontFamily: "Poppins, sans-serif",
                        letterSpacing: 1,
                        fontSize: { xs: "1.8rem", sm: "2rem", md: "2.25rem" },
                      }}
                    >
                      {status.value}
                    </Typography>
                  </Box>
                </Paper>
              </Fade>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default SuperAdminDashboard;
